import { useEffect, useState } from 'react';

export default function MaintenancePage({ message, subMessage }) {
  const [dots, setDots] = useState('');

  const displayMessage = message || "We're performing scheduled maintenance to improve your experience.";
  const displaySubMessage = subMessage || "The site will be back up and running very soon.";

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 600);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="maintenance-page" style={styles.wrapper}>
      {/* Animated background grain */}
      <div style={styles.grain} />

      {/* Content */}
      <div style={styles.container}>
        {/* Logo */}
        <img
          src="/ltv-vod-logo.png"
          alt="LTV Production"
          style={styles.logo}
          onError={(e) => {
            e.target.style.display = 'none';
            document.getElementById('fallback-logo').style.display = 'block';
          }}
        />
        <h1
          id="fallback-logo"
          style={{ ...styles.fallbackLogo, display: 'none' }}
        >
          LTV Production
        </h1>

        {/* Pulsing icon */}
        <div style={styles.iconWrap}>
          <svg
            style={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>

        {/* Heading */}
        <h2 style={styles.heading}>
          We're Currently Under Maintenance
        </h2>

        {/* Subtext */}
        <p style={styles.subtext}>
          {displayMessage}
          <br />
          {displaySubMessage}
        </p>

        {/* Animated status */}
        <div style={styles.statusBar}>
          <span style={styles.statusDot} />
          <span style={styles.statusText}>
            Maintenance in progress{dots}
          </span>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Footer info */}
        <p style={styles.footer}>
          Thank you for your patience.
          <br />
          <span style={styles.footerMuted}>
            If you need immediate assistance, contact us at{' '}
            <a
              href="mailto:support@ltvproduction.com"
              style={styles.link}
            >
              support@ltvproduction.com
            </a>
          </span>
        </p>

        {/* Admin access — subtle link */}
        <a
          href="/admin"
          data-testid="maintenance-admin-login"
          style={styles.adminLink}
          onMouseEnter={(e) => { e.target.style.color = 'rgba(255,255,255,0.5)'; }}
          onMouseLeave={(e) => { e.target.style.color = 'rgba(255,255,255,0.15)'; }}
        >
          Admin
        </a>
      </div>

      <style>{keyframes}</style>
    </div>
  );
}

const keyframes = `
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.3); }
  }
  @keyframes float-icon {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  @keyframes grain-shift {
    0%, 100% { transform: translate(0, 0); }
    10% { transform: translate(-2%, -2%); }
    30% { transform: translate(1%, 3%); }
    50% { transform: translate(-3%, 1%); }
    70% { transform: translate(2%, -1%); }
    90% { transform: translate(-1%, 2%); }
  }
`;

const styles = {
  wrapper: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0a0a',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  grain: {
    position: 'absolute',
    inset: '-50%',
    width: '200%',
    height: '200%',
    background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
    opacity: 0.4,
    animation: 'grain-shift 8s steps(10) infinite',
    pointerEvents: 'none',
  },
  container: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    maxWidth: '520px',
    padding: '0 24px',
  },
  logo: {
    height: '48px',
    marginBottom: '40px',
    opacity: 0.9,
    filter: 'brightness(1.1)',
  },
  fallbackLogo: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    marginBottom: '40px',
  },
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    marginBottom: '28px',
    animation: 'float-icon 3s ease-in-out infinite',
  },
  icon: {
    width: '28px',
    height: '28px',
    color: '#e5e5e5',
  },
  heading: {
    fontSize: 'clamp(22px, 4vw, 30px)',
    fontWeight: 700,
    color: '#ffffff',
    margin: '0 0 14px 0',
    letterSpacing: '-0.3px',
    lineHeight: 1.25,
  },
  subtext: {
    fontSize: '15px',
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.55)',
    margin: '0 0 32px 0',
  },
  statusBar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '999px',
    padding: '10px 20px',
    marginBottom: '32px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#f59e0b',
    animation: 'pulse-dot 1.5s ease-in-out infinite',
    flexShrink: 0,
  },
  statusText: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: '0.2px',
    minWidth: '180px',
    textAlign: 'left',
  },
  divider: {
    width: '48px',
    height: '1px',
    background: 'rgba(255,255,255,0.1)',
    margin: '0 auto 24px',
  },
  footer: {
    fontSize: '13px',
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.45)',
    margin: 0,
  },
  footerMuted: {
    color: 'rgba(255,255,255,0.3)',
  },
  link: {
    color: 'rgba(255,255,255,0.5)',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  adminLink: {
    display: 'inline-block',
    marginTop: '48px',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.15)',
    textDecoration: 'none',
    letterSpacing: '0.5px',
    transition: 'color 0.2s',
  },
};
